<template>
    <div v-if="cartStore.currentOrderData.customfields_data && cartStore.currentOrderData.customfields_data[eventID]">
      <InputSelect
      v-if="customField.fieldType === 'dropdown'"
      :options="fieldlistValues?.map(x => ({ label: x, value: x })) ?? []"
      :placeholder="placeholderString"
      class="w-full"
      :id="customField.name"
      v-model="customField.value"
      :name="customField.name"
      :required="isRequired"
      :rules="setRules(customField, 'string', isRequired)"
      />

      <InputDate
      v-if="customField.fieldType === 'datefield'"
      :placeholder="placeholderString"
      class="w-full"
      :id="customField.name"
      v-model="customField.value"
      :name="customField.name"
      :required="isRequired"
      :rules="setRules(customField, 'string', isRequired)"
      :min-date="new Date((fieldDef?.minDate ?? 0) * 1000)"
      :max-date="new Date((fieldDef?.maxDate ?? 0) * 1000)"
      :close-on-select="true"
      />

      <InputCPR
      v-if="customField.fieldType === 'cprnumber'"
      :placeholder="placeholderString"
      :id="customField.name"
      v-model="customField.value"
      class="w-full"
      :name="customField.name"
      :required="isRequired"
      :rules="setRules(customField, 'string', isRequired)"
      />

      <InputText
      v-if="customField.fieldType === 'textfield'"
      :placeholder="placeholderString" 
      class="w-full"
      :type="'text'"
      :id="customField.name"
      v-model="customField.value"
      :required="isRequired"
      :rules="setRules(customField, 'string', isRequired)"
      :name="customField.name"
      />

      <InputTextarea
      v-if="customField.fieldType === 'textarea'"
      :placeholder="placeholderString" 
      class="w-full"
      :id="customField.name"
      v-model="customField.value"
      :required="isRequired"
      :rules="setRules(customField, 'string', isRequired)"
      :name="customField.name"
      />

      <InputCheckbox
      v-if="customField.fieldType === 'checkbox'"
      class="w-full"
      checked-border-class="border-accent"
      checked-checkmark-class="text-accent"
      :id="customField.name"
      v-model="customField.value"
      :name="customField.name"
      :label="placeholderString"
      :required="isRequired"
      :rules="setRules(customField, 'boolean', isRequired)"
      />

      <div v-if="customField.fieldType === 'radio'" class="flex flex-col gap-2">
      <InputRadio
          v-for="(option, index) in (fieldlistValues ?? [])"
          :key="option"
          :hide-error="index !== (fieldlistValues?.length || 1) - 1"
          :label="placeholderString"
          :value="option"
          v-model="customField.value"
          :name="customField.name"
          :id="slugify(`${customField.name}-${option}`)"
          class="w-full"
          :rules="setRules(customField, 'string', isRequired)"
          :required="isRequired"
      />
      </div>
    </div>
</template>
<script setup lang="ts">

  import { useFormContext } from 'vee-validate';
  import { computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import * as yup from 'yup';

  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import type { CustomFieldOrderDataEntry } from '@/types';
  import { getRules } from '@/utils/customFieldRules';
  import { formatCustomFieldDescription } from '@/utils/formatCustomFieldDescription';
  import { slugify } from '@/utils/slugify';

  import InputCheckbox from '../ui/inputs/InputCheckbox.vue';
  import InputCPR from '../ui/inputs/InputCPR.vue';
  import InputDate from '../ui/inputs/InputDate.vue';
  import InputRadio from '../ui/inputs/InputRadio.vue';
  import InputSelect from '../ui/inputs/InputSelect.vue';
  import InputText from '../ui/inputs/InputText.vue';
  import InputTextarea from '../ui/inputs/InputTextarea.vue';

  const cartStore = useCartStore();
  const checkoutStore = useCheckoutStore();

    const props = defineProps<{
        type: 'upsaleTicket' | 'ticket';
        fieldName: string;
        ticketId: number | undefined;
        eventID: string;
    }>();

  const { t } = useI18n();

  if(!cartStore.currentOrderData.customfields_data || !cartStore.currentOrderData.customfields_data[props.eventID] || !cartStore.currentOrderData.customfields_data[props.eventID][props.fieldName]) {
    throw new Error(`Custom field data not found for eventID: ${props.eventID}, fieldName: ${props.fieldName}`);
  }
  const customField = cartStore.currentOrderData.customfields_data[props.eventID][props.fieldName];

  // Resolve field definition from the definitions store — not stored on entries to avoid sending over the wire
  const fieldDef = computed(() =>
    cartStore.customfields.custom_fields.find(f => f.name === customField.fieldName) ||
    cartStore.customfields.custom_fields_perticket.find(f => f.name === customField.fieldName) ||
    cartStore.customfields.custom_fields_pertickettype.find(f => f.name === customField.fieldName)
  );
  const fieldlistValues = computed(() =>
    customField.fieldlistID != null
      ? cartStore.customfields.fieldlists[customField.fieldlistID.toString()]?.values ?? null
      : null
  );

  const hasBackendError = computed(() => checkoutStore.customFieldErrors.includes(props.fieldName));

  const backendErrorMessage = computed(() => {
    if (customField.fieldType === 'cprnumber') return t('validation.cpr');
    if (customField.fieldType === 'datefield') return t('validation.required');
    return t('validation.invalid_option');
  });

  const form = useFormContext();
  watch(hasBackendError, (isError) => {
    if (isError) {
      form?.setFieldError(props.fieldName, backendErrorMessage.value);
    }
  }, { immediate: true });
  
  function setRules(customField: CustomFieldOrderDataEntry, fieldType: 'string' | 'number' | 'boolean', required?: boolean) {
    const rules: yup.AnySchema | undefined = getRules(customField, fieldType, required, fieldlistValues.value, fieldDef.value?.char_limit, fieldDef.value?.only_numbers);
    cartStore.setCustomFieldRule(props.fieldName, rules);
    return rules;
  }

  let placeholderString = " ";
  placeholderString = fieldDef.value?.description ? fieldDef.value.description : placeholderString;
  placeholderString = fieldDef.value?.long_description ? fieldDef.value.long_description : placeholderString;
  placeholderString = formatCustomFieldDescription(placeholderString);

  const isRequired = computed(() => {
    if(customField.fieldType === 'checkbox') {
      return fieldDef.value?.must_be_checked;
    }
    return !fieldDef.value?.empty_ok;
  });

  console.log('Rendering CheckoutCustomField with props:', props);
  console.log('Custom field data:', customField);

  if(!customField) {
    throw new Error(`Custom field data not found for eventID: ${props.eventID}, fieldName: ${props.fieldName}`);
  }


</script>